/**
 * @fileoverview Hook for fetching Sudoku puzzles
 * @description Fetches random puzzles by difficulty from the database
 */

import { useState, useCallback } from "react";
import { supabase } from "@/lib/supabase/client";
import { SudokuPuzzle, SudokuDifficulty } from "../types";

interface UseSudokuPuzzlesReturn {
  /** Loading state */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Fetch a random puzzle by difficulty */
  fetchRandomPuzzle: (difficulty: SudokuDifficulty) => Promise<SudokuPuzzle | null>;
  /** Fetch a specific puzzle by ID */
  fetchPuzzleById: (id: string) => Promise<SudokuPuzzle | null>;
  /** Fetch puzzle counts per difficulty */
  fetchPuzzleCounts: () => Promise<Record<SudokuDifficulty, number>>;
}

/**
 * Hook for managing Sudoku puzzle fetching
 * @returns Puzzle fetching functions and state
 */
export const useSudokuPuzzles = (): UseSudokuPuzzlesReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetches a random puzzle for the given difficulty
   */
  const fetchRandomPuzzle = useCallback(
    async (difficulty: SudokuDifficulty): Promise<SudokuPuzzle | null> => {
      setIsLoading(true);
      setError(null);

      try {
        // First get count of puzzles for this difficulty
        const { count, error: countError } = await supabase
          .from("sudoku_puzzles")
          .select("*", { count: "exact", head: true })
          .eq("difficulty", difficulty)
          .eq("is_active", true);

        if (countError) throw countError;

        if (!count || count === 0) {
          throw new Error(`Geen puzzels gevonden voor ${difficulty}`);
        }

        // Get a random offset
        const randomOffset = Math.floor(Math.random() * count);

        // Fetch one puzzle at that offset
        const { data, error: fetchError } = await supabase
          .from("sudoku_puzzles")
          .select("*")
          .eq("difficulty", difficulty)
          .eq("is_active", true)
          .range(randomOffset, randomOffset)
          .single();

        if (fetchError) throw fetchError;

        return data as SudokuPuzzle;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Fout bij ophalen puzzel";
        setError(message);
        console.error("[useSudokuPuzzles] fetchRandomPuzzle error:", err);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  /**
   * Fetches a specific puzzle by ID
   */
  const fetchPuzzleById = useCallback(
    async (id: string): Promise<SudokuPuzzle | null> => {
      setIsLoading(true);
      setError(null);

      try {
        const { data, error: fetchError } = await supabase
          .from("sudoku_puzzles")
          .select("*")
          .eq("id", id)
          .single();

        if (fetchError) throw fetchError;

        return data as SudokuPuzzle;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Puzzel niet gevonden";
        setError(message);
        console.error("[useSudokuPuzzles] fetchPuzzleById error:", err);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  /**
   * Fetches the count of available puzzles per difficulty
   */
  const fetchPuzzleCounts = useCallback(async (): Promise<
    Record<SudokuDifficulty, number>
  > => {
    try {
      const difficulties: SudokuDifficulty[] = ["easy", "medium", "hard"];
      const counts: Record<SudokuDifficulty, number> = {
        easy: 0,
        medium: 0,
        hard: 0,
      };

      await Promise.all(
        difficulties.map(async (difficulty) => {
          const { count, error } = await supabase
            .from("sudoku_puzzles")
            .select("*", { count: "exact", head: true })
            .eq("difficulty", difficulty)
            .eq("is_active", true);

          if (!error && count !== null) {
            counts[difficulty] = count;
          }
        })
      );

      return counts;
    } catch (err) {
      console.error("[useSudokuPuzzles] fetchPuzzleCounts error:", err);
      return { easy: 0, medium: 0, hard: 0 };
    }
  }, []);

  return {
    isLoading,
    error,
    fetchRandomPuzzle,
    fetchPuzzleById,
    fetchPuzzleCounts,
  };
};

